import React, { useEffect } from 'react';
import { createPortal } from 'react-dom';
import { portalRoot } from '../../lib/portalRoot';
import { Button } from './Button';

// ConfirmDialog — small portal'd confirmation modal used for state changes
// that the user might do accidentally (priority changes, estimate-status
// changes, etc.). Designed to feel light and respectful rather than
// heavyweight — single sentence in the body, two-button footer, ESC and
// backdrop-click dismiss.
//
// Props:
//   open           — boolean; nothing renders when false
//   title          — short uppercase heading ("CHANGE PRIORITY?", etc.)
//   children       — body JSX (use colored spans for from/to context)
//   confirmLabel   — default "Confirm"; pass the action verb for clarity
//                    (e.g. "Change Priority", "Change Status")
//   onConfirm      — called when the user clicks the confirm button
//   onCancel       — called on cancel button, backdrop click, or ESC
//   secondaryLabel — optional; renders a third (middle) button for an
//                    alternate action (e.g. "Change estimate only" next to
//                    "Shift jobs"). When set, onSecondary is called.
//   onSecondary    — handler for the secondary button.
//   danger         — destructive confirm (feedback.md 2B): the confirm
//                    button is the outlined-red `danger` variant, focus
//                    starts on Cancel, and the title should NAME the record
//                    ("Delete Marcus Hill?") with the body saying what
//                    happens and what is kept.
//
// Under 768px the dialog is a bottom sheet with full-width stacked buttons.
// With a secondaryLabel (three buttons) the dialog widens to max-w-lg and
// the footer wraps, so a long label never runs past the edge (2026-09-22).

export default function ConfirmDialog({ open, title, children, confirmLabel = 'Confirm', cancelLabel = 'Cancel', secondaryLabel, onConfirm, onSecondary, onCancel, danger = false }) {
    useEffect(() => {
        if (!open) return;
        const onKey = (e) => { if (e.key === 'Escape') onCancel?.(); };
        document.addEventListener('keydown', onKey);
        return () => document.removeEventListener('keydown', onKey);
    }, [open, onCancel]);

    if (!open) return null;

    return createPortal(
        <div
            role="dialog"
            aria-modal="true"
            className="fixed inset-0 z-[1100] flex items-end md:items-center justify-center bg-slate-900/40 backdrop-blur-sm"
            onClick={onCancel}
            onMouseDown={(e) => e.stopPropagation()}>
            <div
                className={`bg-white shadow-2xl border border-slate-200 w-full ${secondaryLabel ? 'md:max-w-lg' : 'md:max-w-sm'} md:w-[90%] md:mx-4 overflow-hidden rounded-t-2xl md:rounded-2xl ${danger ? 'border-t-4 border-t-red-600 md:border-t' : ''}`}
                onClick={(e) => e.stopPropagation()}>
                <div className="px-5 pt-5 pb-3">
                    <h3 className="font-lemonmilk text-base text-slate-900 uppercase tracking-wide">
                        {title}
                    </h3>
                    <div className="text-sm text-slate-600 mt-2 leading-relaxed">
                        {children}
                    </div>
                </div>
                <div className="flex flex-col-reverse md:flex-row md:flex-wrap items-stretch md:items-center gap-2 px-5 py-3 pb-[max(env(safe-area-inset-bottom),12px)] md:pb-3 bg-slate-50 border-t border-slate-200">
                    <Button
                        variant="tertiary"
                        onClick={onCancel}
                        autoFocus={danger}
                        className="w-full md:flex-1">
                        {cancelLabel}
                    </Button>
                    {secondaryLabel && (
                        <Button
                            variant="secondary"
                            onClick={onSecondary}
                            className="w-full md:flex-1">
                            {secondaryLabel}
                        </Button>
                    )}
                    <Button
                        variant={danger ? 'danger' : 'primary'}
                        onClick={onConfirm}
                        autoFocus={!danger}
                        className="w-full md:flex-1">
                        {confirmLabel}
                    </Button>
                </div>
            </div>
        </div>,
        portalRoot(),
    );
}
